import { Injectable, inject } from '@angular/core';
import { Observable, from, of, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { CashPaymentConfigService } from '@app/api/order-service/api/cashPaymentConfig.service';
import type { CashPaymentMaxAmountConfigDTO } from '@app/api/order-service/model/cashPaymentMaxAmountConfigDTO';
import type { CashPaymentMaxAmountConfigVO } from '@app/api/order-service/model/cashPaymentMaxAmountConfigVO';
import { BlobConverterUtil } from '@shared/utils/blob-converter.util';

const JSON_ACCEPT_OPTIONS = { httpHeaderAccept: 'application/json' } as const;

/** UI-facing alias kept for consistency with other order-service config wrappers. */
export type CashPaymentMaxAmountConfig = CashPaymentMaxAmountConfigVO;

@Injectable({ providedIn: 'root' })
export class CashPaymentMaxAmountService {
  private readonly api = inject(CashPaymentConfigService);

  getConfig(): Observable<CashPaymentMaxAmountConfig> {
    return this.api
      .getCashPaymentMaxAmount('body', false, JSON_ACCEPT_OPTIONS as any)
      .pipe(
        switchMap((response: CashPaymentMaxAmountConfigVO | Blob) =>
          this.handleBlobResponse<CashPaymentMaxAmountConfigVO>(response),
        ),
        catchError((error) => this.handleBlobError(error)),
      );
  }

  upsertConfig(
    dto: CashPaymentMaxAmountConfigDTO,
  ): Observable<CashPaymentMaxAmountConfig> {
    return this.api
      .upsertCashPaymentMaxAmount(dto, 'body', false, JSON_ACCEPT_OPTIONS as any)
      .pipe(
        switchMap((response: CashPaymentMaxAmountConfigVO | Blob) =>
          this.handleBlobResponse<CashPaymentMaxAmountConfigVO>(response),
        ),
        catchError((error) => this.handleBlobError(error)),
      );
  }

  private handleBlobResponse<T>(response: Blob | T): Observable<T> {
    if (response instanceof Blob) {
      return from(BlobConverterUtil.convertToJson(response)) as Observable<T>;
    }
    return of(response as T);
  }

  private handleBlobError(error: unknown): Observable<never> {
    const err = error as { error?: unknown };
    if (err?.error instanceof Blob) {
      return from(BlobConverterUtil.convertToJson(err.error)).pipe(
        catchError(() => throwError(() => error)),
        switchMap((parsedError: unknown) =>
          throwError(() => ({ ...(error as object), error: parsedError })),
        ),
      );
    }
    return throwError(() => error);
  }
}
